import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import { SpeedUnit } from '../../types';

interface SpeedometerProps {
  speed: number;
  speedUnit: SpeedUnit;
  acceleration: number;
  maxSpeed?: number;
}

export const IceComfortSpeedometer: React.FC<SpeedometerProps> = ({
  speed,
  speedUnit,
  maxSpeed = 260,
}) => {
  const needleRef = useRef<SVGGElement | null>(null);
  const speedTextRef = useRef<HTMLSpanElement | null>(null);
  const prevSpeedRef = useRef<number>(0);

  // Angle maps from -135deg (0 km/h) to +135deg (maxSpeed) -> 270 deg range
  const clampedSpeed = Math.max(0, Math.min(maxSpeed, speed));
  const targetAngle = -135 + (clampedSpeed / maxSpeed) * 270;

  useEffect(() => {
    if (!needleRef.current) return;
    anime({
      targets: needleRef.current,
      transform: `rotate(${targetAngle} 150 150)`,
      duration: 550,
      easing: 'easeOutElastic(1, .8)',
    });
  }, [targetAngle]);

  useEffect(() => {
    if (!speedTextRef.current) return;
    const obj = { val: prevSpeedRef.current };
    anime({
      targets: obj,
      val: speed,
      round: 1,
      duration: 450,
      easing: 'easeOutQuad',
      update: () => {
        if (speedTextRef.current) {
          speedTextRef.current.textContent = String(Math.round(obj.val));
        }
      },
    });
    prevSpeedRef.current = speed;
  }, [speed]);

  // Generate 27 tick marks (every 10 km/h)
  const ticks = Array.from({ length: 27 }, (_, i) => {
    const tickSpeed = i * 10;
    const angle = -135 + (tickSpeed / maxSpeed) * 270;
    const isMajor = tickSpeed % 20 === 0;
    const rad = (angle - 90) * (Math.PI / 180);
    const rOuter = 135;
    const rInner = isMajor ? 118 : 124;
    const x1 = 150 + rInner * Math.cos(rad);
    const y1 = 150 + rInner * Math.sin(rad);
    const x2 = 150 + rOuter * Math.cos(rad);
    const y2 = 150 + rOuter * Math.sin(rad);

    const isLit = tickSpeed <= clampedSpeed;

    return {
      key: i,
      x1,
      y1,
      x2,
      y2,
      isMajor,
      isLit,
    };
  });

  return (
    <div className="relative w-72 h-72 md:w-84 md:h-84 flex flex-col items-center justify-center select-none">
      {/* Luxury Gauge Dial Plate */}
      <div className="absolute inset-2 rounded-full bg-gradient-to-b from-slate-900 via-[#0a0d14] to-slate-950 border border-slate-700/50 shadow-[inset_0_4px_24px_rgba(0,0,0,0.8),0_8px_32px_rgba(0,0,0,0.6)]" />

      {/* Ticks and Needle SVG */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 300">
        {/* Outer Bezel */}
        <circle
          cx="150"
          cy="150"
          r="140"
          fill="none"
          stroke="rgba(148, 163, 184, 0.15)"
          strokeWidth="2"
        />

        {/* Illuminated Tick Marks */}
        {ticks.map((t) => (
          <line
            key={t.key}
            x1={t.x1}
            y1={t.y1}
            x2={t.x2}
            y2={t.y2}
            stroke={t.isLit ? '#38bdf8' : 'rgba(100, 116, 139, 0.35)'}
            strokeWidth={t.isMajor ? 3 : 1.5}
            strokeLinecap="round"
            className="transition-colors duration-150"
          />
        ))}

        {/* Mechanical Physical Needle */}
        <g ref={needleRef} transform={`rotate(-135 150 150)`}>
          <polygon
            points="148,150 152,150 150.5,35 149.5,35"
            fill="#38bdf8"
            className="filter drop-shadow-[0_0_6px_rgba(56,189,248,0.8)]"
          />
          <circle cx="150" cy="150" r="12" fill="#0f172a" stroke="#38bdf8" strokeWidth="2.5" />
          <circle cx="150" cy="150" r="4" fill="#38bdf8" />
        </g>
      </svg>

      {/* Central Digital Readout */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center mt-18">
        <span
          ref={speedTextRef}
          className="text-5xl md:text-6xl font-black font-display text-white tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
        >
          {Math.round(speed)}
        </span>
        <span className="text-[11px] font-mono tracking-widest text-sky-400 font-bold uppercase mt-0.5">
          {speedUnit.toUpperCase()}
        </span>
        <span className="text-[9px] font-mono text-slate-400 tracking-wider uppercase mt-1">
          COMFORT DRIVE
        </span>
      </div>
    </div>
  );
};
